import {
  addMonths,
  format,
  getYear,
  isSameDay,
  isSameMonth,
  setMonth,
  setYear,
  startOfDay,
  startOfMonth,
  subMonths,
} from 'date-fns';
import { CalendarIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import {
  forwardRef,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
  type CSSProperties,
  type FC,
  type HTMLAttributes,
  type ReactNode,
} from 'react';
import { type CustomComponents, type DateRange, type Matcher } from 'react-day-picker';
import { type PortaledSurfaceVariant } from '../lib/portaledSurface';
import { cn } from '../lib/utils';
import { Button } from './Button';
import { Popover, PopoverContent, PopoverTrigger } from './Popover';
import { buttonVariants } from './ui/button';
import { Calendar } from './ui/calendar';

// `Calendar` (react-day-picker's `DayPicker`) does not take a plain `date-fns` `Locale` —
// its `locale` prop is `Partial<DayPickerLocale>`, a `date-fns` locale *extended* with
// translated month/nav/day-name labels for the calendar's own UI (see `enUS`/`fr`/`es`
// re-exported from `react-day-picker/locale` below). Deriving the type from `Calendar`'s
// own declared prop gets this right without hand-declaring `Partial<DayPickerLocale>`.
export type DatePickerLocale = ComponentProps<typeof Calendar>['locale'];

// Re-exported so consumers never need their own `react-day-picker` dependency just to pass
// a translated locale to `DatePicker` — kept to the locales this app actually uses rather
// than the full ~120-locale catalogue `react-day-picker/locale` ships.
export type { DateRange } from 'react-day-picker';
export { enUS, es, fr } from 'react-day-picker/locale';

export interface DatePickerSingleProps {
  mode: 'single';
  selected: Date | undefined;
  onSelect: (date: Date | undefined) => void;
}

export interface DatePickerRangeProps {
  mode: 'range';
  selected: DateRange | undefined;
  onSelect: (range: DateRange | undefined) => void;
}

export type DatePickerProps = (DatePickerSingleProps | DatePickerRangeProps) & {
  /** Days before this one are disabled. */
  minDate?: Date;
  /** Days after this one are disabled — e.g. footage that doesn't exist yet. */
  maxDate?: Date;
  /**
   * `react-day-picker`'s own type only accepts the 0–6 literal union; a plain `number`
   * is taken here so callers can pass an app-wide "first day of week" setting straight
   * through without narrowing it themselves.
   */
  weekStartsOn?: number;
  /** A DayPicker-compatible locale (e.g. this module's own re-exported `fr`) for month/day names and formatting. */
  locale?: DatePickerLocale;
  placeholder?: ReactNode;
  disabled?: boolean;
  className?: string;
  /** Surface theme for the portaled calendar. Use `"dark"` inside `DarkThemeScope`. */
  variant?: PortaledSurfaceVariant;
  /** Applied to the portaled calendar popover (e.g. a high z-index above the video page). */
  contentClassName?: string;
  /** Portal target for the calendar popover. Defaults to `document.body`. */
  container?: ComponentProps<typeof PopoverContent>['container'];
  'data-testid'?: string;
};

function formatDay(date: Date, locale: DatePickerLocale): string {
  // `Calendar`'s `locale` prop type (`Partial<DayPickerLocale>`) is a structural superset
  // of what `date-fns`'s own `format` expects — every locale this component is ever given
  // (`enUS`, `fr`, `es`, ...) is a complete object, never actually partial.
  return format(date, 'PP', { locale } as Parameters<typeof format>[2]);
}

// Takes the full discriminated union (not a `Pick`) so `mode` still narrows `selected` —
// `Pick<DatePickerProps, ...>` would collapse the union and decorrelate the two fields.
function getTriggerLabel(props: DatePickerProps): ReactNode {
  const { locale, placeholder = 'Pick a date' } = props;

  if (props.mode === 'single') {
    return props.selected ? formatDay(props.selected, locale) : placeholder;
  }

  const { selected } = props;
  if (!selected?.from) {
    return placeholder;
  }

  if (!selected.to || isSameDay(selected.from, selected.to)) {
    return formatDay(selected.from, locale);
  }

  return `${formatDay(selected.from, locale)} – ${formatDay(selected.to, locale)}`;
}

function getInitialMonth(props: DatePickerProps): Date {
  const anchor = props.mode === 'single' ? props.selected : props.selected?.from;
  return anchor ?? new Date();
}

// Puts the calendar on the month that holds `anchor`. A month already on screen stays as
// it is, so an update inside that month does not undo the month the user browsed to.
// Range mode shows `current` and the month after it.
function getAnchoredMonth(current: Date, anchor: Date | undefined, isRange: boolean): Date {
  if (!anchor) {
    return current;
  }
  if (isSameMonth(anchor, current) || (isRange && isSameMonth(anchor, addMonths(current, 1)))) {
    return current;
  }
  return startOfMonth(anchor);
}

// Effect deps use calendar-day values, not `Date` / `DateRange` identity. A parent that
// passes a new object for the same day must not re-run the open-draft sync.
function getDayKey(date: Date | undefined): number | undefined {
  return date ? startOfDay(date).getTime() : undefined;
}

function getRangeKey(range: DateRange | undefined): string | undefined {
  if (!range) {
    return undefined;
  }
  return `${getDayKey(range.from)}:${getDayKey(range.to)}`;
}

// Figma's `date` component (node `7075:23311`) spells out a two-letter weekday header
// ("Su Mo Tu We Th Fr Sa") regardless of locale — `date-fns`'s six-`E` token is the one
// format string that produces that short form instead of the single-letter narrow one.
function formatWeekdayName(day: Date, locale: DatePickerLocale): string {
  return format(day, 'EEEEEE', locale ? ({ locale } as Parameters<typeof format>[2]) : undefined);
}

// The calendar is seven 36px cells with 8px gaps: 7 x 36 + 6 x 8 = 300px. The year grid
// matches that column exactly so the popover never changes width when the two views swap.
const CALENDAR_COLUMN_WIDTH = 'w-[18.75rem]';

// Range mode shows two of those columns, each inside the calendar's own 12px padding.
// Pinned so the popover keeps its width when the year grid replaces both calendars.
const RANGE_POPOVER_WIDTH = 'w-[40.5rem]';

// The year grid stands in for the calendar's day table, so it takes that table's exact
// height and the popover does not resize when the two views swap. The table is the 12px
// weekday header (`label-sm` has a 12px line height) plus six 36px week rows. Each week row
// has `mt-2`, including the first, so 12 + (6 x 36) + (6 x 8) = 276px. `fixedWeeks` on every
// `Calendar` is what makes the row count six for every month rather than four to six.
const YEAR_GRID_HEIGHT = 'h-[17.25rem]';

const YEAR_PICKER_FIRST = 1900;
const YEAR_PICKER_LAST = 2099;
const YEAR_PAGE = 12;

function yearPageStartFor(year: number): number {
  const offset = Math.max(0, year - YEAR_PICKER_FIRST);
  return YEAR_PICKER_FIRST + Math.floor(offset / YEAR_PAGE) * YEAR_PAGE;
}

interface CalendarCaptionToggleProps {
  expanded: boolean;
  onClick: () => void;
  children?: ReactNode;
}

/**
 * The month caption doubles as the drill-in toggle, so it is a real `Button` rather than
 * the plain `<span>` `react-day-picker` renders. It keeps the caption's existing appearance
 * and adds the hover and focus treatment every other ghost control has.
 */
const CalendarCaptionToggle = forwardRef<HTMLButtonElement, CalendarCaptionToggleProps>(
  ({ expanded, onClick, children }, ref) => (
    <Button
      ref={ref}
      type="button"
      variant="ghost"
      size="sm"
      onClick={onClick}
      aria-expanded={expanded}
      className={cn(
        'h-8 rounded-lg px-2 text-text-primary label-md',
        'hover:bg-interaction-secondary-background-default',
      )}
    >
      {children}
      <ChevronDownIcon
        className={cn('size-3.5 text-text-muted transition-transform', expanded && 'rotate-180')}
        aria-hidden
      />
    </Button>
  ),
);
CalendarCaptionToggle.displayName = 'CalendarCaptionToggle';

type CalendarDrillView = 'month' | 'year';

interface CalendarPeriodViewProps {
  view: CalendarDrillView;
  /** The year the month grid shows. */
  browseYear: number;
  /** The month on screen when the drill-in opened. Its month stays highlighted. */
  anchorMonth: Date;
  locale: DatePickerLocale;
  minDate?: Date;
  maxDate?: Date;
  onBrowseYear: (year: number) => void;
  onShowMonths: () => void;
  onShowYears: () => void;
  onPickMonth: (monthIndex: number) => void;
}

// Same hover and selected fill as a day cell. `aria-pressed` marks the current month or
// year, and its hover rule outranks the plain hover so the accent fill stays put.
const periodOptionClassName = cn(
  'h-9 w-full rounded-md px-1 text-text-primary label-sm',
  'transition-none',
  'hover:bg-interaction-secondary-background-default hover:text-text-primary',
  'dark:hover:bg-interaction-secondary-background-default dark:hover:text-text-primary',
  'disabled:text-interaction-secondary-content-inactive',
  'aria-pressed:bg-background-accent aria-pressed:text-text-primary',
  'aria-pressed:hover:bg-background-accent',
  'dark:aria-pressed:bg-background-accent dark:aria-pressed:hover:bg-background-accent',
  'dark:aria-pressed:hover:text-text-primary',
);

function monthIsDisabled(
  year: number,
  monthIndex: number,
  minDate: Date | undefined,
  maxDate: Date | undefined,
): boolean {
  const monthStart = new Date(year, monthIndex, 1);
  const monthEnd = new Date(year, monthIndex + 1, 0);
  if (minDate && monthEnd < startOfDay(minDate)) {
    return true;
  }
  if (maxDate && monthStart > startOfDay(maxDate)) {
    return true;
  }
  return false;
}

// The caption drills in one level at a time: days, then months, then a page of 12 years.
// Choosing a year returns to months. Choosing a month returns to that month's days.
const CalendarPeriodView: FC<CalendarPeriodViewProps> = ({
  view,
  browseYear,
  anchorMonth,
  locale,
  minDate,
  maxDate,
  onBrowseYear,
  onShowMonths,
  onShowYears,
  onPickMonth,
}) => {
  const headerRef = useRef<HTMLButtonElement>(null);
  const [yearPageStart, setYearPageStart] = useState(() => yearPageStartFor(browseYear));
  const isYearView = view === 'year';
  const minYear = minDate ? getYear(minDate) : YEAR_PICKER_FIRST;
  const maxYear = maxDate ? getYear(maxDate) : YEAR_PICKER_LAST;
  const pageEnd = Math.min(YEAR_PICKER_LAST, yearPageStart + YEAR_PAGE - 1);
  const canGoPrev = isYearView ? yearPageStart > YEAR_PICKER_FIRST : browseYear > YEAR_PICKER_FIRST;
  const canGoNext = isYearView
    ? yearPageStart + YEAR_PAGE <= YEAR_PICKER_LAST
    : browseYear < YEAR_PICKER_LAST;

  useLayoutEffect(() => {
    // Opening this view unmounts the caption that had focus. Choosing a year unmounts
    // that year button and shows the month grid without remounting this view. Put focus
    // on the header so a keyboard user stays inside the popover. `preventScroll` keeps
    // the page still.
    headerRef.current?.focus({ preventScroll: true });
  }, [view]);

  const months = Array.from({ length: 12 }, (_, index) => ({
    index,
    label: format(
      new Date(browseYear, index, 1),
      'MMM',
      locale ? ({ locale } as Parameters<typeof format>[2]) : undefined,
    ),
  }));
  const years = Array.from({ length: YEAR_PAGE }, (_, index) => yearPageStart + index);

  return (
    <div className={cn('mx-auto flex flex-col gap-3', CALENDAR_COLUMN_WIDTH)}>
      <div className="flex h-9 items-center justify-between">
        <button
          type="button"
          className={cn(
            buttonVariants({ variant: 'ghost' }),
            'size-8 select-none rounded-lg border border-border-muted p-1',
          )}
          aria-label={isYearView ? 'Previous years' : 'Previous year'}
          disabled={!canGoPrev}
          onClick={() =>
            isYearView ? setYearPageStart(yearPageStart - YEAR_PAGE) : onBrowseYear(browseYear - 1)
          }
        >
          <ChevronLeftIcon className="size-4" aria-hidden />
        </button>
        <CalendarCaptionToggle
          ref={headerRef}
          expanded={isYearView}
          onClick={() => {
            if (isYearView) {
              onShowMonths();
              return;
            }
            setYearPageStart(yearPageStartFor(browseYear));
            onShowYears();
          }}
        >
          {isYearView ? `${yearPageStart} \u2013 ${pageEnd}` : browseYear}
        </CalendarCaptionToggle>
        <button
          type="button"
          className={cn(
            buttonVariants({ variant: 'ghost' }),
            'size-8 select-none rounded-lg border border-border-muted p-1',
          )}
          aria-label={isYearView ? 'Next years' : 'Next year'}
          disabled={!canGoNext}
          onClick={() =>
            isYearView ? setYearPageStart(yearPageStart + YEAR_PAGE) : onBrowseYear(browseYear + 1)
          }
        >
          <ChevronRightIcon className="size-4" aria-hidden />
        </button>
      </div>
      <div className={cn('grid grid-cols-4 content-center gap-2', YEAR_GRID_HEIGHT)}>
        {isYearView
          ? years.map((year) => {
              const isSelected = year === getYear(anchorMonth);
              return (
                <Button
                  key={year}
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={year < minYear || year > maxYear || year > YEAR_PICKER_LAST}
                  aria-pressed={isSelected}
                  onClick={() => {
                    onBrowseYear(year);
                    onShowMonths();
                  }}
                  className={periodOptionClassName}
                >
                  {year}
                </Button>
              );
            })
          : months.map((monthOption) => {
              const isSelected =
                browseYear === getYear(anchorMonth) && monthOption.index === anchorMonth.getMonth();
              return (
                <Button
                  key={monthOption.index}
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={monthIsDisabled(browseYear, monthOption.index, minDate, maxDate)}
                  aria-pressed={isSelected}
                  onClick={() => onPickMonth(monthOption.index)}
                  className={periodOptionClassName}
                >
                  {monthOption.label}
                </Button>
              );
            })}
      </div>
    </div>
  );
};

// Apply the Figma tokens and dimensions through the generated shadcn Calendar API.
// This keeps CalendarDayButton, keyboard focus, and date semantics in the shadcn base.
const calendarClassNames = {
  months: 'relative flex',
  month: 'flex flex-col gap-3',
  // The nav row is absolutely positioned at the top of the month. Without a height it
  // collapses to its 32px buttons and centres them 2px above the 36px caption row, so it
  // is given the caption's height and centres its buttons on the same line.
  nav: 'absolute inset-x-0 top-0 flex h-[var(--cell-size)] w-full items-center justify-between gap-1',
  weekdays: 'flex gap-2',
  weekday: 'w-9 flex-none label-sm text-text-muted',
  week: 'flex gap-2 mt-2',
  // `Calendar` spreads this object over its own defaults, so every key here replaces the
  // shadcn string instead of merging with it. `group/day` must therefore be repeated by
  // hand: `CalendarDayButton` draws its keyboard focus ring through
  // `group-data-[focused=true]/day:`, and that variant matches nothing once the group name
  // is dropped.
  day: 'group/day w-9 select-none p-0',
  // `CalendarDayButton` merges this class string last through `cn` (tailwind-merge), so
  // each token below replaces the shadcn default of the same group. No `!` is required.
  day_button: cn(
    'text-text-primary label-sm',
    // The ghost button keeps a translucent dark hover, and `transition-all` animates it.
    // In the dark panel that hover replaces the selected fill while the pointer stays on the day.
    'transition-none',
    'hover:bg-interaction-secondary-background-default hover:text-text-primary',
    'dark:hover:bg-interaction-secondary-background-default dark:hover:text-text-primary',
    'disabled:text-interaction-secondary-content-inactive',
    'data-[selected-single=true]:bg-background-accent',
    'data-[selected-single=true]:hover:bg-background-accent',
    'dark:data-[selected-single=true]:bg-background-accent',
    'dark:data-[selected-single=true]:hover:bg-background-accent',
    'data-[selected-single=true]:text-text-primary',
    'dark:data-[selected-single=true]:hover:text-text-primary',
    'data-[range-start=true]:bg-background-accent',
    'data-[range-start=true]:hover:bg-background-accent',
    'dark:data-[range-start=true]:hover:bg-background-accent',
    'data-[range-middle=true]:bg-background-accent',
    'data-[range-middle=true]:hover:bg-background-accent',
    'dark:data-[range-middle=true]:hover:bg-background-accent',
    'data-[range-end=true]:bg-background-accent',
    'data-[range-end=true]:hover:bg-background-accent',
    'dark:data-[range-end=true]:hover:bg-background-accent',
    'data-[range-start=true]:text-text-primary',
    'data-[range-middle=true]:text-text-primary',
    'data-[range-end=true]:text-text-primary',
  ),
  // Each day button paints its own range fill. The cells stay transparent, so the gap
  // between days shows the surface and the days read as separate chips.
  range_start: '',
  range_middle: '',
  range_end: '',
  // These target the day button from its cell. A descendant selector outranks the
  // button's own plain `text-*` class, and the `data-[selected*]` variants above
  // outrank the descendant selector, so a selected day keeps its own colour.
  outside: '[&_button]:text-text-muted',
  today: '[&_button]:text-text-accent',
  button_previous: cn(
    buttonVariants({ variant: 'ghost' }),
    'size-8 select-none rounded-lg border border-border-muted p-1',
  ),
  button_next: cn(
    buttonVariants({ variant: 'ghost' }),
    'size-8 select-none rounded-lg border border-border-muted p-1',
  ),
};

const calendarStyle = { '--cell-size': '2.25rem' } as CSSProperties;

// The generated `Calendar` declares `Root` and `Chevron` inline. Each render then makes
// new component types, and React rebuilds the whole day grid. A rebuild between
// pointerdown and click drops the click. These module-level copies replace the inline ones.
const CalendarRoot: CustomComponents['Root'] = ({ rootRef, ...props }) => (
  <div data-slot="calendar" ref={rootRef} {...props} />
);

const CalendarChevron: CustomComponents['Chevron'] = ({ className, orientation, ...props }) => {
  const Icon =
    orientation === 'left'
      ? ChevronLeftIcon
      : orientation === 'right'
        ? ChevronRightIcon
        : ChevronDownIcon;
  return <Icon className={cn('size-4', className)} {...props} />;
};

const calendarComponents = { Root: CalendarRoot, Chevron: CalendarChevron };

// `react-day-picker` renders the caption as a plain `<span>`. A `Button` here makes the
// header open the year picker.
function makeCaptionLabel(onOpenYearPicker: () => void): CustomComponents['CaptionLabel'] {
  const CaptionLabel = ({ children }: HTMLAttributes<HTMLSpanElement>) => (
    <CalendarCaptionToggle expanded={false} onClick={onOpenYearPicker}>
      {children}
    </CalendarCaptionToggle>
  );
  CaptionLabel.displayName = 'DatePickerCaptionLabel';
  return CaptionLabel;
}

/**
 * The shadcn Date Picker recipe: Popover + Button + generated Calendar.
 * This wrapper adds the Solink API and Figma-specific layout and confirmation behavior.
 *
 * Per the Figma spec (node `88:1608`), a click on a day only updates a *draft* selection
 * held inside this component — `onSelect` fires (and the popover closes) only on Apply;
 * Cancel, an outside click or Escape all discard the draft. Callers therefore still see
 * an immediate-commit `onSelect`, same as before; the draft never leaks out.
 */
export const DatePicker: FC<DatePickerProps> = (props) => {
  const {
    minDate,
    maxDate,
    weekStartsOn,
    locale,
    disabled,
    className,
    contentClassName,
    variant,
    container,
  } = props;
  const [open, setOpen] = useState(false);
  const [draftSingle, setDraftSingle] = useState<Date | undefined>(
    props.mode === 'single' ? props.selected : undefined,
  );
  const [draftRange, setDraftRange] = useState<DateRange | undefined>(
    props.mode === 'range' ? props.selected : undefined,
  );
  // The draft is a snapshot taken when the popover opens. A parent that updates `selected`
  // from a live source while the popover is open would therefore be reverted by Apply. An
  // untouched draft follows that newer value instead. Once the user picks a day the draft
  // wins, because re-seeding then would discard the selection the user is making.
  const [draftDirty, setDraftDirty] = useState(false);
  const liveSingle = props.mode === 'single' ? props.selected : undefined;
  const liveRange = props.mode === 'range' ? props.selected : undefined;
  const liveSingleKey = getDayKey(liveSingle);
  const liveRangeKey = getRangeKey(liveRange);
  const liveSingleRef = useRef(liveSingle);
  const liveRangeRef = useRef(liveRange);
  liveSingleRef.current = liveSingle;
  liveRangeRef.current = liveRange;
  // Range mode renders two independent, side-by-side single-month `Calendar`s (per the
  // Figma layout — each keeps its own prev/next chevrons); this is the month shown on
  // the left, the right one is always one month ahead. Both modes control the month
  // rather than seeding it once, because the year picker has to know which month is on
  // screen to keep it when only the year changes.
  const [leftMonth, setLeftMonth] = useState<Date>(() => getInitialMonth(props));

  // A re-seeded draft takes the visible month with it. Without this, an update that lands
  // in another month leaves the grid on the old month with no selection on it, so the grid
  // no longer matches the value Apply commits. The updater form keeps `leftMonth` out of
  // the dependencies: the month must follow a new value, not snap back while the user
  // browses.
  useEffect(() => {
    if (!open || draftDirty) {
      return;
    }
    const next = liveSingleRef.current;
    setDraftSingle(next);
    setLeftMonth((current) => getAnchoredMonth(current, next, false));
  }, [open, draftDirty, liveSingleKey]);

  useEffect(() => {
    if (!open || draftDirty) {
      return;
    }
    const next = liveRangeRef.current;
    setDraftRange(next);
    setLeftMonth((current) => getAnchoredMonth(current, next?.from, true));
  }, [open, draftDirty, liveRangeKey]);

  const handleDraftSingle = (date: Date | undefined) => {
    setDraftDirty(true);
    setDraftSingle(date);
  };

  const handleDraftRange = (range: DateRange | undefined) => {
    setDraftDirty(true);
    setDraftRange(range);
  };
  // `day` shows the calendar. `month` and `year` replace it. The anchor is the calendar
  // whose caption opened the drill-in, so a choice returns to that month, not always the left one.
  const [pickerView, setPickerView] = useState<'day' | CalendarDrillView>('day');
  const [pickerAnchor, setPickerAnchor] = useState<'left' | 'right'>('left');
  const [browseYear, setBrowseYear] = useState(() => getYear(getInitialMonth(props)));
  const leftMonthRef = useRef(leftMonth);
  leftMonthRef.current = leftMonth;
  const openMonthViewRef = useRef<(anchor: 'left' | 'right') => void>(() => {});
  openMonthViewRef.current = (anchor) => {
    const shown = anchor === 'right' ? addMonths(leftMonthRef.current, 1) : leftMonthRef.current;
    setPickerAnchor(anchor);
    setBrowseYear(getYear(shown));
    setPickerView('month');
  };

  // A new `components` value each render gives the caption a new component type, and
  // React then rebuilds the caption. Each side keeps one object for the life of the picker.
  const leftComponents = useMemo(
    () => ({
      ...calendarComponents,
      CaptionLabel: makeCaptionLabel(() => openMonthViewRef.current('left')),
    }),
    [],
  );
  const rightComponents = useMemo(
    () => ({
      ...calendarComponents,
      CaptionLabel: makeCaptionLabel(() => openMonthViewRef.current('right')),
    }),
    [],
  );

  const label = getTriggerLabel(props);
  const hasSelection = props.mode === 'single' ? !!props.selected : !!props.selected?.from;
  // `react-day-picker`'s `weekStartsOn` only accepts the 0–6 literal union; values
  // outside that range are a caller bug, not something this wrapper can validate.
  const dayPickerWeekStartsOn = weekStartsOn as 0 | 1 | 2 | 3 | 4 | 5 | 6 | undefined;
  const calendarDisabled: Matcher[] = [];
  if (minDate) {
    calendarDisabled.push({ before: minDate });
  }
  if (maxDate) {
    calendarDisabled.push({ after: maxDate });
  }
  const weekdayFormatters = {
    formatWeekdayName: (day: Date) => formatWeekdayName(day, locale),
  };

  const rightMonth = addMonths(leftMonth, 1);
  const anchorMonth = pickerAnchor === 'right' ? rightMonth : leftMonth;

  const handlePickMonth = (monthIndex: number) => {
    // Normalize to the first before changing the year. A displayed month can retain the
    // selected day (for example February 29), and `setYear` would otherwise overflow it
    // into March when the target year is not a leap year.
    const picked = setMonth(setYear(startOfMonth(anchorMonth), browseYear), monthIndex);
    setLeftMonth(pickerAnchor === 'right' ? subMonths(picked, 1) : picked);
    setPickerView('day');
  };

  const handleOpenChange = (next: boolean) => {
    if (next) {
      // Re-seed the draft from the last *committed* selection every time the popover
      // opens — not just on mount — so a stale draft from a cancelled prior open (or a
      // prop change while closed) never resurfaces.
      if (props.mode === 'single') {
        setDraftSingle(props.selected);
      } else {
        setDraftRange(props.selected);
      }
      setDraftDirty(false);
      setLeftMonth(getInitialMonth(props));
      // Always reopen on the day grid, never on a month or year grid left up from last time.
      setPickerView('day');
    }
    setOpen(next);
  };

  const handleApply = () => {
    if (props.mode === 'single') {
      props.onSelect(draftSingle);
    } else {
      props.onSelect(draftRange);
    }
    setOpen(false);
  };

  // Draft state simply isn't re-seeded until the next open (see `handleOpenChange`), so
  // dismissing here is enough to discard it — nothing to reset.
  const handleCancel = () => setOpen(false);

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          data-empty={!hasSelection}
          className={cn(
            // The default button size uses radius-lg. This control is a field, so it uses the input radius.
            'w-full justify-start gap-2 rounded font-normal data-[empty=true]:text-foreground-secondary',
            className,
          )}
          data-testid={props['data-testid']}
        >
          <CalendarIcon className="size-4 shrink-0" aria-hidden />
          <span className="truncate">{label}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        variant={variant}
        container={container}
        className={cn(
          'w-auto overflow-hidden rounded-lg bg-background-surface p-0',
          contentClassName,
        )}
        align="start"
      >
        {props.mode === 'single' ? (
          <div className="flex flex-col items-center gap-3 p-3">
            {pickerView === 'day' ? (
              <Calendar
                mode="single"
                required
                selected={draftSingle}
                onSelect={handleDraftSingle}
                month={leftMonth}
                onMonthChange={setLeftMonth}
                disabled={calendarDisabled.length > 0 ? calendarDisabled : undefined}
                weekStartsOn={dayPickerWeekStartsOn}
                locale={locale}
                formatters={weekdayFormatters}
                classNames={calendarClassNames}
                fixedWeeks
                components={leftComponents}
                className="p-0"
                style={calendarStyle}
                autoFocus
              />
            ) : (
              <CalendarPeriodView
                view={pickerView}
                browseYear={browseYear}
                anchorMonth={anchorMonth}
                locale={locale}
                minDate={minDate}
                maxDate={maxDate}
                onBrowseYear={setBrowseYear}
                onShowMonths={() => setPickerView('month')}
                onShowYears={() => setPickerView('year')}
                onPickMonth={handlePickMonth}
              />
            )}
            <div className="flex w-full items-center justify-between">
              <Button type="button" variant="ghost" onClick={handleCancel}>
                Cancel
              </Button>
              <Button type="button" variant="default" onClick={handleApply}>
                Apply
              </Button>
            </div>
          </div>
        ) : (
          <div className={cn('flex flex-col', RANGE_POPOVER_WIDTH)}>
            {pickerView === 'day' ? (
              <div className="flex">
                <Calendar
                  mode="range"
                  selected={draftRange}
                  onSelect={handleDraftRange}
                  month={leftMonth}
                  onMonthChange={setLeftMonth}
                  disabled={calendarDisabled.length > 0 ? calendarDisabled : undefined}
                  weekStartsOn={dayPickerWeekStartsOn}
                  locale={locale}
                  formatters={weekdayFormatters}
                  classNames={calendarClassNames}
                  fixedWeeks
                  showOutsideDays={false}
                  components={leftComponents}
                  style={calendarStyle}
                  autoFocus
                />
                <Calendar
                  mode="range"
                  selected={draftRange}
                  onSelect={handleDraftRange}
                  month={rightMonth}
                  onMonthChange={(month) => setLeftMonth(subMonths(month, 1))}
                  disabled={calendarDisabled.length > 0 ? calendarDisabled : undefined}
                  weekStartsOn={dayPickerWeekStartsOn}
                  locale={locale}
                  formatters={weekdayFormatters}
                  classNames={calendarClassNames}
                  fixedWeeks
                  showOutsideDays={false}
                  components={rightComponents}
                  style={calendarStyle}
                />
              </div>
            ) : (
              <div className="p-3">
                <CalendarPeriodView
                  view={pickerView}
                  browseYear={browseYear}
                  anchorMonth={anchorMonth}
                  locale={locale}
                  minDate={minDate}
                  maxDate={maxDate}
                  onBrowseYear={setBrowseYear}
                  onShowMonths={() => setPickerView('month')}
                  onShowYears={() => setPickerView('year')}
                  onPickMonth={handlePickMonth}
                />
              </div>
            )}
            <div className="flex items-center justify-between p-3">
              <Button type="button" variant="ghost" onClick={handleCancel}>
                Cancel
              </Button>
              <Button type="button" variant="default" onClick={handleApply}>
                Apply
              </Button>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
